import { useEffect, useMemo, useRef } from 'react';

import { GetAvailableDatesQuery, PassengerTypeSearchParameter } from '@websky/graphql';
import { LocationsConfig } from '../../../../../../types';
import { getMinPricesRequestVariables, getPrices } from '../../../../../utils';
import { useMinPricesInPeriod } from '@shared/lib/results';
import { useCurrency } from '../../../../../../hooks';

export const useDatepickerMinPrices = ({
	locations,
	passengers,
	legId,
	isRT,
	schedule
}: {
	locations: LocationsConfig;
	passengers: PassengerTypeSearchParameter[];
	legId: number;
	isRT?: boolean;
	schedule?: {
		to?: GetAvailableDatesQuery;
		back?: GetAvailableDatesQuery;
	};
}) => {
	const minPricesInPeriodParams = useMemo(() => {
		return getMinPricesRequestVariables(locations, passengers);
	}, [locations, passengers]);

	const { data, loading, refetch: refetchPrices, getPrices: getMinPrices } = useMinPricesInPeriod({
		fetchPolicy: 'no-cache',
		variables: { parameters: minPricesInPeriodParams },
		skip: !locations.arrival || !locations.departure || locations.arrival?.iata === locations.departure?.iata
	});

	const scheduleTo = schedule?.to?.FlightsSchedule?.datesData;
	const scheduleBack = schedule?.back?.FlightsSchedule?.datesData;

	const minPrices = useMemo(() => {
		return {
			pricesTo: getPrices(getMinPrices(0, isRT), scheduleTo),
			pricesBack: getPrices(getMinPrices(1, isRT), scheduleBack)
		};
	}, [isRT, getMinPrices, scheduleTo, scheduleBack]);

	const { currency } = useCurrency();
	const previousCurrency = useRef(currency);

	useEffect(() => {
		if (previousCurrency.current === currency) {
			return;
		}

		previousCurrency.current = currency;
		refetchPrices({ parameters: minPricesInPeriodParams });
	}, [currency, minPricesInPeriodParams]);

	return {
		data,
		minPrices,
		pricesByLeg: getPrices(getMinPrices(legId, isRT), legId === 0 ? scheduleTo : scheduleBack),
		loading,
		refetchPrices
	};
};
